import { useEffect, useState } from "react";
import { Copy, Loader2, Table2 } from "lucide-react";
import { toast } from "sonner";
import { getFoundationSchemaTables, getFoundationSchemaSpec } from "@/lib/api";

// Read-only column specs for the Foundation staging tables (PO Sync, J/C
// Change Order Sync) and the AR tables — bridge v2.5 `diag` reads of
// INFORMATION_SCHEMA / sys.*. No write is built from here.
export default function StagingSchemaPanel() {
  const [tables, setTables] = useState([]);
  const [table, setTable] = useState("pending_po_sync_header");
  const [spec, setSpec] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { getFoundationSchemaTables().then((r) => setTables(r.data)).catch(() => {}); }, []);

  const load = (t) => {
    setLoading(true); setError(null); setSpec(null);
    getFoundationSchemaSpec(t).then((r) => setSpec(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not read the schema"))
      .finally(() => setLoading(false));
  };

  return (
    <div className="mt-3 rounded-md border border-slate-200 p-2" data-testid="fs-schema-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide inline-flex items-center gap-1"><Table2 className="w-3 h-3" /> Staging table specs (read-only)</div>
          <div className="text-[11px] text-slate-500">Every NOT NULL column without a default is required. Columns with no CHECK constraint on their legal values are flagged for a Practice test. Nothing here writes.</div>
        </div>
        <div className="flex items-center gap-1.5">
          <select value={table} onChange={(e) => setTable(e.target.value)} className="h-7 text-[11px] border border-slate-300 rounded px-1 bg-white" data-testid="fs-schema-table-select">
            {(tables.length ? tables : [{ table, purpose: "" }]).map((t) => <option key={t.table} value={t.table}>{t.table}</option>)}
          </select>
          <button type="button" onClick={() => load(table)} disabled={loading} className="h-7 px-2 text-[11px] rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50" data-testid="fs-schema-load">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Read schema
          </button>
          {spec?.markdown && (
            <button type="button" onClick={() => { navigator.clipboard?.writeText(spec.markdown); toast.success("Spec copied as Markdown"); }} className="h-7 px-2 text-[11px] rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1" data-testid="fs-schema-copy-md">
              <Copy className="w-3 h-3" /> Copy Markdown
            </button>
          )}
        </div>
      </div>
      {error && <div className="mt-1.5 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="fs-schema-error">{error}</div>}
      {spec && (
        <div className="mt-1.5 text-[11px]" data-testid="fs-schema-spec">
          <div className="text-slate-700 mb-1">
            <strong>{spec.table}</strong> — {spec.purpose}. {spec.columns.length} columns · <strong>{spec.required.length} required</strong> · PK {spec.primary_key.join(", ") || "—"}
            {spec.practice_test_required.length > 0 && <span className="text-amber-900"> · Practice test needed for: {spec.practice_test_required.join(", ")}</span>}
          </div>
          <div className="max-h-96 overflow-auto rounded border border-slate-200">
            <table className="w-full text-[10px]">
              <thead className="bg-slate-50 sticky top-0"><tr>{["#", "column", "type", "null", "default", "required", "FK →", "CHECK", "note"].map((h) => <th key={h} className="text-left px-1.5 py-1 font-semibold text-slate-600">{h}</th>)}</tr></thead>
              <tbody>
                {spec.columns.map((c, i) => (
                  <tr key={c.name} className={`border-t border-slate-100 ${c.required ? "bg-rose-50/40" : ""}`} data-testid={`fs-schema-col-${c.name}`}>
                    <td className="px-1.5 py-0.5 text-slate-400">{i + 1}</td>
                    <td className="px-1.5 py-0.5 font-mono">{c.name}{c.primary_key ? <span className="ml-1 text-[9px] text-sky-700">PK</span> : null}</td>
                    <td className="px-1.5 py-0.5">{c.type}</td>
                    <td className="px-1.5 py-0.5">{c.nullable ? "Y" : "N"}</td>
                    <td className="px-1.5 py-0.5 font-mono">{c.default || ""}</td>
                    <td className="px-1.5 py-0.5 font-semibold text-rose-700">{c.required ? "yes" : ""}</td>
                    <td className="px-1.5 py-0.5">{c.foreign_keys.join(", ")}</td>
                    <td className="px-1.5 py-0.5 font-mono">{c.checks.join("; ")}</td>
                    <td className="px-1.5 py-0.5 text-amber-900">{c.note || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
